import { helpers, type PluginConfiguration } from '../helpers'
import { icons } from '../icons'

const TRANSLATION_DEBUG_STORAGE_KEY = 'fu-devtools-translations'

type TranslationDebugSettings = {
  showKeys: boolean
  showLocalazyLinks: boolean
  checkVariables: boolean
}

const defaultSettings: TranslationDebugSettings = {
  showKeys: false,
  showLocalazyLinks: false,
  checkVariables: false,
}

const loadSettings = (): TranslationDebugSettings => {
  try {
    const stored = window.localStorage.getItem(TRANSLATION_DEBUG_STORAGE_KEY)
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) }
    }
  } catch {
    /* localStorage may not be available */
  }
  return defaultSettings
}

const currentSettings = loadSettings()

const createToggle = (id: string, label: string, description: string, checked: boolean) =>
  helpers.createHTMLElements([
    {
      element: 'label',
      className: 'inline-flex gap-2 items-center cursor-pointer text-white',
      content: () =>
        helpers.createHTMLElements([
          {
            element: 'input',
            attributes: {
              type: 'checkbox',
              ...(checked ? { checked: '' } : {}),
              'data-fu-tools-translation-toggle': id,
            },
            className: 'sr-only peer',
          },
          {
            element: 'div',
            className:
              "relative w-7 h-4 bg-[#4a5565] rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-[#d1d5dc] after:border after:rounded-full after:w-3 after:h-3 after:transition-all peer-checked:bg-[#00d3f3]",
          },
          {
            element: 'span',
            content: label,
          },
        ]),
    },
    {
      element: 'div',
      className: 'text-sm text-[#6F91B7] ml-9',
      content: description,
    },
  ])

const config: PluginConfiguration = {
  name: 'Translations',
  id: 'translations',
  icon: icons.translations,
  extra: '',
  layout: {
    type: 'full',
    render: () => {
      const createdElements = helpers.createHTMLElements([
        {
          className:
            'pt-4 px-4 flex items-center justify-between mb-2 text-white gap-2 font-semibold',
          content: 'Translation Debug',
        },
        {
          className: 'px-4 pb-3 text-sm text-[#6F91B7] flex flex-col gap-1.5',
          content: () =>
            helpers.createHTMLElements([
              {
                element: 'div',
                content:
                  'Tools to help find and fix translations. Toggle the options below and click Apply — the page will reload with debug info visible.',
              },
              {
                element: 'div',
                className: 'mt-1 border-t border-[#4a5565] pt-2 flex flex-col gap-1',
                content: () =>
                  helpers.createHTMLElements([
                    {
                      element: 'div',
                      className: 'font-semibold text-[#8BA4C0]',
                      content: 'How to read the debug output:',
                    },
                    {
                      element: 'div',
                      content:
                        '\u{1F511} Raw keys — every translated text is replaced by its internal ID (e.g. "Administration.title"). Useful to find out which key a specific text belongs to.',
                    },
                    {
                      element: 'div',
                      content:
                        '\u{270F}\u{FE0F} Localazy links — a small "Edit" button appears next to each visible translation so you can jump straight to Localazy and fix it. For texts used as plain strings (e.g. tooltips, placeholders), a [URL] is appended instead because a clickable button cannot be placed inside a string.',
                    },
                    {
                      element: 'div',
                      content:
                        '\u{26A0}\u{FE0F} Variable check — a warning symbol (\u26A0) appears next to a translation when the translated text is missing a placeholder that the code expects (e.g. {count} or <bold>). For plain-string translations, [!] is appended instead. This usually means the translation needs to be updated in Localazy.',
                    },
                  ]),
              },
            ]),
        },
        {
          className: 'flex items-center gap-2 mx-2',
          content: () =>
            helpers.createHTMLElements([
              {
                className:
                  'flex flex-1 justify-center border-0 text-white! transition-all bg-[#4a5565] hover:bg-[#364153]! rounded-md cursor-pointer p-2',
                content: '🚀 Apply',
                title: 'Save settings and reload the page',
                id: 'fu-tools-translations-apply',
              },
              {
                className:
                  'flex flex-1 justify-center border-0 text-white! transition-all bg-[#4a5565] hover:bg-[#364153]! rounded-md cursor-pointer p-2',
                content: '🧹 Reset',
                title: 'Clear all translation debug settings and reload',
                id: 'fu-tools-translations-reset',
              },
            ]),
        },
        {
          className: 'flex flex-col gap-4 p-4',
          content: () => {
            const fragment = document.createDocumentFragment()
            fragment.appendChild(
              createToggle(
                'showKeys',
                'Show raw translation keys',
                'Display message IDs instead of translated text, along with declared variables/wrappers.',
                currentSettings.showKeys
              )
            )
            fragment.appendChild(
              createToggle(
                'showLocalazyLinks',
                'Show Localazy links',
                'Add clickable "Edit" links (FormattedMessage) or [URL] suffix (formatMessage) pointing to Localazy.',
                currentSettings.showLocalazyLinks
              )
            )
            fragment.appendChild(
              createToggle(
                'checkVariables',
                'Check variables/wrappers',
                'Warn when a translation is missing declared {variable} or <wrapper> placeholders.',
                currentSettings.checkVariables
              )
            )
            return fragment
          },
        },
      ])

      const tempDiv = document.createElement('div')
      tempDiv.appendChild(createdElements)
      return tempDiv.outerHTML
    },
  },
  init: () => {
    const applyButton = document.querySelector('#fu-tools-translations-apply')
    applyButton?.addEventListener('click', () => {
      const toggles = document.querySelectorAll<HTMLInputElement>(
        '[data-fu-tools-translation-toggle]'
      )
      const settings: Record<string, boolean> = {}
      for (const toggle of toggles) {
        const key = toggle.getAttribute('data-fu-tools-translation-toggle')
        if (key) {
          settings[key] = toggle.checked
        }
      }
      try {
        window.localStorage.setItem(TRANSLATION_DEBUG_STORAGE_KEY, JSON.stringify(settings))
      } catch {
        /* ignore */
      }
      window.location.reload()
    })

    const resetButton = document.querySelector('#fu-tools-translations-reset')
    resetButton?.addEventListener('click', () => {
      try {
        window.localStorage.removeItem(TRANSLATION_DEBUG_STORAGE_KEY)
      } catch {
        /* ignore */
      }
      window.location.reload()
    })
  },
  render: () => config.layout.render({}),
}

export const translations = config
